import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Row, Col, message, theme, Skeleton, Card, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getAutoChargeSettings,
  getBillingDetails,
  getDefaultPaymentMethod,
  getPayments,
  getProducts,
  updateAutoChargeSettings
} from './BillingServices';
import Plans from './Plans/Plans';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { useTheme } from '../Shared/GlobalContext/ThemeContext';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import PaymentDetailModal from './PaymentDetailModal/PaymentDetailModal';
import { DEFAULT_PAGINATION } from './constants';
import { formatPayments } from './utils';
import { Action, Resource } from '../../Constants/socket';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import useSocketEvent from '../../Hooks/useSocketEvent';
import useSocket from '../../Hooks/useSocket';

// Component imports
import {
  BalanceCard,
  PaymentInfoCard,
  AutoReloadCard,
  PaymentHistory,
  SettingsModal
} from './components';

const Billing = () => {
  const { user } = useGlobal();
  const { isDarkMode } = useTheme();
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { handleError } = useErrorHandler();
  const { socket } = useSocket();

  // Table columns for skeleton loading
  const columns = [
    { title: t('date'), dataIndex: 'date', key: 'date' },
    { title: t('amount'), dataIndex: 'amount', key: 'amount' },
    { title: t('status'), dataIndex: 'status', key: 'status' },
    { title: t('actions'), dataIndex: 'actions', key: 'actions' }
  ];

  // State management
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [products, setProducts] = useState([]);
  const [autoChargeSettings, setAutoChargeSettings] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [paymentMethodInfo, setPaymentMethodInfo] = useState(null);
  const [isSettingsVisible, setIsSettingsVisible] = useState(false);
  const [isPaymentDetailModalVisible, setIsPaymentDetailModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [paymentTableFilters, setPaymentTableFilters] = useState({});
  const [chipFilters, setChipFilters] = useState([]);
  const [normalizedChipFilters, setNormalizedChipFilters] = useState({});
  const [paymentSorter, setPaymentSorter] = useState({});
  const [paymentSortBy, setPaymentSortBy] = useState(undefined);
  const [paymentSortDirection, setPaymentSortDirection] = useState(undefined);
  const [paymentSearchString, setPaymentSearchString] = useState('');


  // Separate loading states for each UI section
  const [isBalanceLoading, setIsBalanceLoading] = useState(false);
  const [isPaymentInfoLoading, setIsPaymentInfoLoading] = useState(false);
  const [isAutoReloadLoading, setIsAutoReloadLoading] = useState(false);
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);

  // Card styling
  const cardStyle = {
    backgroundColor: token.colorBgContainer,
    color: token.colorText,
    border: isDarkMode ? `1px solid ${token.colorBorder}` : 'none',
  };

  // API calls
  const fetchPayments = async (page, size, filters, sorter, search_string) => {
    try {
      setPaymentsLoading(true);
      const response = await getPayments({
        page,
        size,
        filters,
        sorter,
        sort_by: paymentSortBy,
        sort_direction: paymentSortDirection,
        search_string,
      });

      if (response.success) {
        const formattedPayments = formatPayments(response.data);

        setPayments(formattedPayments);
        setPagination({
          current: response.metadata.page,
          pageSize: response.metadata.page_size,
          total: response.metadata.total,
        });
      } else {
        const text = resolveBackendMessage(t, response);
        if (text) message.error(text);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setPaymentsLoading(false);
    }
  };
  const fetchProductData = async () => {
    setIsBalanceLoading(true);
    try {
      const { data } = await getProducts();
      setProducts(data);
    } catch (error) {
      handleError(error);
    } finally {
      setIsBalanceLoading(false);
    }
  };

  const fetchAutoChargeSettings = async () => {
    try {
      setIsAutoReloadLoading(true);
      const { data } = await getAutoChargeSettings();
      setAutoChargeSettings(data);
    } catch (error) {
      handleError(error);
    } finally {
      setIsAutoReloadLoading(false);
    }
  };

  const fetchPaymentMethodInfo = async () => {
    try {
      setIsPaymentInfoLoading(true);
      const response = await getDefaultPaymentMethod();
      setPaymentMethodInfo(response?.data ?? null);
    } catch (error) {
      handleError(error);
    } finally {
      setIsPaymentInfoLoading(false);
    }
  };

  const fetchBillingDetails = async () => {
    try {
      setIsPaymentInfoLoading(true);
      const { data } = await getBillingDetails();
      setBillingDetails(data);
      window.open(data.customer_portal_url, '_blank');
    } catch (error) {
      handleError(error);
    } finally {
      setIsPaymentInfoLoading(false);
    }
  };

  const handleUpdateSettings = async (values, form = null) => {
    try {
      setIsSettingsLoading(true);
      const { data: updatedSettings, ...payload } = await updateAutoChargeSettings(values);
      setAutoChargeSettings(updatedSettings);
      {
        const text = resolveBackendMessage(t, payload);
        if (text) message.success(text);
      }
      setIsSettingsVisible(false);
    } catch (error) {
      handleError(error, form);
    } finally {
      setIsSettingsLoading(false);
    }
  };

  // Event handlers
  const handleTableChange = (pagination, filters, sorter, search_string = paymentSearchString) => {
    setPaymentTableFilters(filters || {});
    setPaymentSorter(sorter || {});
    setPaymentSearchString(typeof search_string === 'string' ? search_string : '');
    if (!sorter || !sorter.field || !sorter.order) {
      setPaymentSortBy(undefined);
      setPaymentSortDirection(undefined);
    } else {
      setPaymentSortBy(sorter.field);
      setPaymentSortDirection(sorter.order === 'ascend' ? 'asc' : 'desc');
    }
    const combinedFilters = {
      ...(filters || {}),
      ...(normalizedChipFilters || {}),
    };
    fetchPayments(
      pagination.current,
      pagination.pageSize,
      combinedFilters,
      sorter,
      typeof search_string === 'string' ? search_string : ''
    );
  };

  const handleChipFiltersChange = useCallback((nextFilters, normalized) => {
    setChipFilters(nextFilters);
    setNormalizedChipFilters(normalized);
    setPaymentTableFilters((prev) => prev || {});
    // Re-fetch from page 1 when chip filters change
    const combinedFilters = {
      ...(paymentTableFilters || {}),
      ...(normalized || {}),
    };
    fetchPayments(1, pagination.pageSize, combinedFilters, paymentSorter, paymentSearchString);
  }, [paymentTableFilters, pagination.pageSize, paymentSorter, paymentSearchString]);

  const handleChipSortChange = useCallback(({ sort_by, sort_direction }) => {
    setPaymentSortBy(sort_by);
    setPaymentSortDirection(sort_direction);
    const combinedFilters = {
      ...(paymentTableFilters || {}),
      ...(normalizedChipFilters || {}),
    };
    fetchPayments(1, pagination.pageSize, combinedFilters, paymentSorter, paymentSearchString);
  }, [paymentTableFilters, normalizedChipFilters, pagination.pageSize, paymentSorter, paymentSearchString]);

  const handleViewPaymentDetails = (payment) => {
    setSelectedPayment(payment);
    setIsPaymentDetailModalVisible(true);
  };

  const handleAddPaymentMethod = async () => {
    if (!billingDetails) {
      await fetchBillingDetails();
    } else {
      window.open(billingDetails.customer_portal_url, '_blank');
    }
  };

  const openSettingsModal = (event) => {
    event.stopPropagation();
    setIsSettingsVisible(true);
  };

  const closeSettingsModal = (event) => {
    event.stopPropagation();
    setIsSettingsVisible(false);
  };

  // Initial data loading
  useEffect(() => {
    fetchPayments(pagination.current, pagination.pageSize, undefined, undefined, paymentSearchString);
  }, []);

  useEffect(() => {
    fetchProductData();
  }, []);

  useEffect(() => {
    fetchAutoChargeSettings();
  }, []);

  useEffect(() => {
    fetchPaymentMethodInfo();
  }, []);

  // Subscribe to user-scoped room to receive CREATE events
  useSocketSubscription(Resource.USER, user?.id ? [user.id] : null, true);

  // Subscribe to specific payment IDs to receive UPDATE events
  const paymentIds = useMemo(() => payments.map(p => p.id).filter(Boolean), [payments]);
  const paymentCursors = useMemo(() => payments.filter(payment => payment.id).map(payment => ({ id: payment.id, fromTimestamp: payment.updated_at })), [payments]);
  useSocketSubscription(Resource.PAYMENT, paymentIds.length > 0 ? paymentIds : null, false, null, paymentCursors);

  // Efficiently merge updated payment rows
  const handlePaymentUpdate = useCallback((data) => {
    setPayments((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updatedRow = formatPayments([data])[0];
      return prev.map((p) => (p.id === updatedRow.id ? { ...p, ...updatedRow } : p));
    });
  }, []);

  useSocketEvent(Resource.PAYMENT, Action.UPDATE, handlePaymentUpdate);

  // Handle new payment creation
  const handlePaymentCreate = useCallback((data) => {
    const newRow = formatPayments([data])[0];
    setPayments((prev) => {
      if (!Array.isArray(prev)) return [newRow];
      if (prev.some(p => p.id === newRow.id)) return prev;
      return [newRow, ...prev];
    });
    setPagination((prev) => ({ ...prev, total: (prev?.total || 0) + 1 }));

    // Subscribe to new payment from its creation time to cover the readiness gap
    if (data?.id && data?.created_at && socket) {
      socket.subscribeToResource(Resource.PAYMENT, [data.id], data.created_at);
    }
  }, [socket]);

  useSocketEvent(Resource.PAYMENT, Action.CREATE, handlePaymentCreate);

  // Handle payment settings updates — merge inline for real-time auto-reload state
  const handlePaymentSettingUpdated = useCallback((data) => {
    setAutoChargeSettings(prev => prev ? { ...prev, ...data } : data);
  }, []);

  useSocketEvent(Resource.PAYMENT_SETTING, Action.UPDATE, handlePaymentSettingUpdated);


  return (
    <>
      {isSettingsVisible && (
        <SettingsModal
          isVisible={isSettingsVisible}
          onCancel={closeSettingsModal}
          onSubmit={handleUpdateSettings}
          autoChargeSettings={autoChargeSettings}
          products={products}
          loading={isSettingsLoading}
        />
      )}

      {isModalVisible && (
        <Plans
          handleClose={() => setIsModalVisible(false)}
          autoChargeSettings={autoChargeSettings}
        />
      )}

      {isPaymentDetailModalVisible && (
        <PaymentDetailModal
          payment={selectedPayment}
          visible={isPaymentDetailModalVisible}
          onClose={() => setIsPaymentDetailModalVisible(false)}
          products={products}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          {isBalanceLoading ? (
            <Card style={cardStyle}>
              <Skeleton active paragraph={{ rows: 2 }} />
            </Card>
          ) : (
            <BalanceCard
              user={user}
              onAddCoins={() => setIsModalVisible(true)}
              cardStyle={cardStyle}
            />
          )}
        </Col>

        <Col xs={24} sm={12} lg={8}>
          {isPaymentInfoLoading ? (
            <Card style={cardStyle}>
              <Skeleton active paragraph={{ rows: 2 }} />
            </Card>
          ) : (
            <PaymentInfoCard
              billingDetails={paymentMethodInfo}
              cardStyle={cardStyle}
              onAddPaymentMethod={handleAddPaymentMethod}
              loading={isPaymentInfoLoading}
            />
          )}
        </Col>

        <Col xs={24} sm={24} lg={8}>
          {isAutoReloadLoading ? (
            <Card style={cardStyle}>
              <Skeleton active paragraph={{ rows: 2 }} />
            </Card>
          ) : (
            <AutoReloadCard
              autoChargeSettings={autoChargeSettings}
              products={products}
              onOpenSettings={openSettingsModal}
              cardStyle={cardStyle}
            />
          )}
        </Col>
      </Row>

      <Row style={{ marginTop: '16px' }}>
        <Col span={24}>
          <PaymentHistory
            payments={payments}
            loading={paymentsLoading}
            pagination={pagination}
            onTableChange={handleTableChange}
            onViewPaymentDetails={handleViewPaymentDetails}
            chipFilters={chipFilters}
            onChipFiltersChange={handleChipFiltersChange}
            sortValue={{ sort_by: paymentSortBy, sort_direction: paymentSortDirection }}
            onSortChange={handleChipSortChange}
            exportFilters={{
              ...(paymentTableFilters || {}),
              ...(normalizedChipFilters || {}),
            }}
            cardStyle={cardStyle}
          />
        </Col>
      </Row>
    </>
  );
};

export default Billing;
